import { Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ReviewPlanEmployee, ReviewPlanVerbose } from '../../models/review-plan.model';
import { EmployeeContactCard } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/container/employee-layout/components/employee-contact-card/models/employee-contact-card.model';
import { TranslateService } from '@ngx-translate/core';
import { ReviewPlansService } from '../../services/review-plans.service';
import { defer, finalize, forkJoin, from, switchMap } from 'rxjs';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SelectEmployeesDialogComponent } from '@app/shared/components/select-employees-dialog/select-employees-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { EmployeeContactCardService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/container/employee-layout/components/employee-contact-card/services/employee-contact-card.service';

@Component({
  selector: 'app-review-plan-employees',
  templateUrl: './review-plan-employees.component.html',
  styleUrls: ['./review-plan-employees.component.scss']
})
export class ReviewPlanEmployeesComponent implements OnInit {
  @Input() reviewPlan: ReviewPlanVerbose;
  @ViewChild('excelexport') excelexport: any;

  excelColumns: any[] = [
    { field: 'firstname', title: this.translate.instant('ReviewPlanEmployees-FirstName') },
    { field: 'lastname', title: this.translate.instant('ReviewPlanEmployees-LastName') },
    { field: 'positions', title: this.translate.instant('ReviewPlanEmployees-Positions') },
    { field: 'managers', title: this.translate.instant('ReviewPlanEmployees-Managers') },
    { field: 'workLocations', title: this.translate.instant('ReviewPlanEmployees-WorkLocations') },
    { field: 'organization', title: this.translate.instant('ReviewPlanEmployees-Organization') },
  ];

  isLoadingEmployees: boolean;
  employeesGridDataResult: { data: ReviewPlanEmployee[]; total: number; };
  pageSize: number = 5;
  skip: number = 0;
  public columns: any[] = [
    { field: "employee", title: this.translate.instant('ReviewPlanEmployees-Name'), type: 'employee' },
  ];
  clearSelectedItems: boolean = false;
  searchValue: string;
  searchFilterString: string;
  getUsersRequest: any;
  selectEmployeesDialogRef: any;
  excelData: any[];
  fileName: string = 'ReviewPlanEmployees.xlsx';

  constructor(
    public translate: TranslateService,
    private reviewPlansService: ReviewPlansService,
    private dialog: MatDialog,
    private overlayService: OverlayService,
    private snackbarService: SnackbarService,
    private ngZone: NgZone,
    private employeeContactCardService: EmployeeContactCardService,
  ) { }

  ngOnInit(): void {
    this.getEmployees();
  }

  getEmployees() {
    this.isLoadingEmployees = true;

    this.getUsersRequest = this.reviewPlansService.getReviewPlanEmployees(this.reviewPlan.id, this.pageSize, this.skip, null, this.searchFilterString)
      .pipe(
        finalize(() => {
          this.isLoadingEmployees = false;
        })
      )
      .subscribe(
        (res) => {
          this.employeesGridDataResult = {
            data: res.data,
            total: res.total,
          }
        }
      );
  }

  public allData() {
    this.overlayService.show('Loading');

    from(this.reviewPlansService.getAllReviewPlanEmployees(this.reviewPlan.id, null, this.searchFilterString))
      .pipe(
        switchMap((res) => {
          let allEmployees = res.data;
          let requests = [];

          while (allEmployees.length > 0) {
            let employeesToRequest = allEmployees.splice(0, 40);
            const requestFilter = employeesToRequest.map((emp) => `(id = "${emp.employee.id}")`).join(' or ');
            requests.push(this.employeeContactCardService.getAllEmployeeContactCardsAsync(null, requestFilter, true));
          }

          return forkJoin(requests);
        })
      )
      .subscribe({
        next: (res) => {
          this.excelData = res.map(r => r.data).flat();

          this.excelData.map((item) => {
            item.positions = item.positions?.map((position) => position.name).join(', ');
            item.managers = item.managers?.map((manager) => `${manager.firstName} ${manager.lastName}`).join(', ');
            item.workLocations = item.workLocations?.map((location) => location.name).join(', ');
            item.organization = item.organization?.name;
          });

          // if I dont have this timeout first time I download the report the data will be blank
          setTimeout(() => {
            this.excelexport.save();
          }, 1000);
        },
        error: (e) => {
          this.overlayService.hide();
        },
        complete: () => this.overlayService.hide()
      });
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.getEmployees();
  }

  showAddUserModal() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      nonSelectableEmployees: this.employeesGridDataResult?.data.map(reviewPlanEmployee => reviewPlanEmployee.employee.id)
    };

    this.ngZone.runOutsideAngular(() => {
      this.selectEmployeesDialogRef = this.dialog.open(SelectEmployeesDialogComponent, dialogConfig);
    });

    const sub = this.selectEmployeesDialogRef.componentInstance.emitSelectedEmployees.subscribe((event) => {
      this.addEmployees(event)
    });
  }

  addEmployees(selectedEmployees: string[]) {
    const observables = selectedEmployees.map(selectedUserId => defer(() => this.reviewPlansService.addReviewPlanEmployee(this.reviewPlan.id, { employeeId: selectedUserId })));

    this.overlayService.show();

    forkJoin(observables)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe(
        (res) => {
          this.getEmployees();
          this.selectEmployeesDialogRef.close();
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
        }
      );
  }

  deleteAllSelected(employees: string[]) {
    const observables = employees.map(employee => defer(() => this.reviewPlansService.deleteReviewPlanEmployee(this.reviewPlan.id, employee)));

    this.overlayService.show();

    forkJoin(observables)
      .pipe(finalize(() => this.overlayService.hide()))
      .subscribe(
        (res) => {
          this.clearSelectedItems = !this.clearSelectedItems;
          this.getEmployees();
          this.snackbarService.openSnackBar(this.translate.instant('DeletedSuccessfully'), 'clear', 'success');
        }
      );
  }

  search(newSearchString: string) {
    this.searchValue = newSearchString;
    this.searchFilterString = '';
    this.skip = 0;

    let variants = this.searchValue.split(' ').filter(value => value);
    variants.map(variant => {
      if (variant) {
        if (this.searchFilterString.length > 2) {
          this.searchFilterString += ' AND ';
        }
        this.searchFilterString += `((EmployeeFirstName like "${variant}") OR (EmployeeLastName like "${variant}"))`;
      }
    });

    this.getUsersRequest.unsubscribe();
    this.getEmployees();
  }
}
